import ReactDOM from "react-dom/client";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { HashRouter, Route, Routes } from "react-router-dom";
import { initializeIcons } from "@fluentui/react";
import NoPage from "./pages/NoPage";
import "./index.css";
import { AppContextProvider, useAppStateContext } from "./state/AppContextProvider";
import { ConfigProvider } from "./config/ConfigProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./theme/ThemeProvider";
import { MuiLogin } from "./pages/layout/MuiLogin";
import { MuiChat } from "./pages/chat/MuiChat";
import { MuiLayout } from "./pages/layout/MuiLayout";
import RedirectLandingPage from "./pages/layout/RedirectLandingPage";
import RedirectLandingPageV2 from "./pages/layout/RedirectLandingPageV2";
import RedirectLandingPageV3 from "./pages/layout/RedirectLandingPageV3";
import { MuiAdmin } from "./pages/layout/MuiAdmin";
import LoadingSpinner from "./components/common/LoadingSpinner";
import SubscriptionSuccess from "./pages/subscription/SubscriptionSuccess";
import SubscriptionCancel from "./pages/subscription/SubscriptionCancel";
// OAuth Auth
import { OAuthProvider, useOAuth } from "./auth/OAuthProvider";
import { OAuthCallback } from "./pages/auth/OAuthCallback";
import HttpServiceProvider from "./state/HttpProvider";
import AccessProvider from "./state/AccessProvider";
import { CookiesProvider } from "react-cookie";
import { DownloadNotificationProvider } from "./components/DownloadNotificationProvider";
import { SuccessProvider } from "./state/SuccessProvider";
import { Box } from "@mui/material";

// Initialize Fluent UI icons
initializeIcons();
// Create a React Query client
const queryClient = new QueryClient();

// Full screen loading spinner component
const FullScreenLoader = () => (
  <Box
    component="div"
    sx={{
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "background.default",
    }}
  >
    <LoadingSpinner text="Loading" />
  </Box>
);

// Component to handle routing logic
const AppRoutes: React.FC = () => {
  const { user, loading: oauthLoading } = useOAuth();
  const { isApproved, isAdmin, isDeveloper, hasPlaygroundAccess, loadingApprovalStatus } = useAppStateContext();

  console.log('🎯 AppRoutes State:', {
    oauthLoading,
    userEmail: user?.email || null,
    userRoles: user?.roles || [],
    loadingApprovalStatus,
    isApproved,
    isAdmin
  });

  // Handle public routes that bypass authentication
  const isPublicRoute = window.location.hash === '#/auth/callback' || 
                        window.location.hash === '#/subscription-success' || 
                        window.location.hash === '#/subscription-cancel';

  if (isPublicRoute) {
    console.log('📍 Public route detected, rendering without auth check');
    return (
      <Routes>
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
      </Routes>
    );
  }

  // CRITICAL: Show loading spinner until ALL checks are complete
  // Step 1: Wait for OAuth authentication check to complete
  if (oauthLoading) {
    console.log('⏳ Step 1: Checking authentication...');
    return <FullScreenLoader />;
  }

  // Step 2: If user exists, wait for approval/role status to load
  if (user && loadingApprovalStatus) {
    console.log('⏳ Step 2: User authenticated, loading roles and approval status...');
    return <FullScreenLoader />;
  }

  // All loading complete - now route based on user state
  console.log('✅ All checks complete, routing based on state');

  // Case 1: No user - show login
  if (!user) {
    console.log('🔓 No authenticated user - showing login page');
    return (
      <Routes>
        <Route path="*" element={<MuiLogin />} />
      </Routes>
    );
  }

  // Case 2: User authenticated but not approved (no "approved" role) - show landing page
  if (!isApproved) {
    console.log('⚠️ User not approved - showing landing/subscription page');
    return (
      <Routes>
        <Route path="/subscription-success" element={<SubscriptionSuccess />} />
        <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
        <Route path="/subscription-v2" element={<RedirectLandingPageV2 />} />
        <Route path="/subscription-v3" element={<RedirectLandingPageV3 />} />
        <Route path="*" element={<RedirectLandingPage />} />
      </Routes>
    );
  }
  
  // Case 3: User authenticated and approved - show main app
  console.log('✅ User authenticated and approved - showing main app');
  return (
    <Routes>
      <Route path="/" element={<MuiLayout />}>
        <Route index element={<MuiChat />} />
        <Route path="*" element={<NoPage />} />
      </Route>
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
      {(isAdmin || isDeveloper || hasPlaygroundAccess) && <Route path="/admin" element={<MuiAdmin />} />}
    </Routes>
  );
};

// Main App component
const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <ConfigProvider>
      <OAuthProvider>
        <HttpServiceProvider>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <ThemeProvider>
              <AccessProvider>
                <AppContextProvider>
                  <SuccessProvider>
                    <DownloadNotificationProvider>
                      <HashRouter
                        future={{
                          v7_startTransition: true,
                          v7_relativeSplatPath: true
                        }}
                      >
                        <AppRoutes />
                      </HashRouter>
                    </DownloadNotificationProvider>
                  </SuccessProvider>
                </AppContextProvider>
              </AccessProvider>
            </ThemeProvider>
          </CookiesProvider>
        </HttpServiceProvider>
      </OAuthProvider>
    </ConfigProvider>
  </QueryClientProvider>
);

// Render the application
ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(<App />);